import React, { useState, useEffect } from 'react';
import AlbumView from './components/AlbumView';
import ErrorModal from './components/ErrorModal';
import { Album } from './types';
import { ApiService, getEventCodeFromUrl } from './services/api';
import { generateEventMetaData, setDefaultMetaTags, updateMetaTags } from './utils/metaTags';
import './App.css';


// Mobile-specific orientation detection utility
const isMobileLandscape = (): boolean => {
  const width = window.innerWidth || document.documentElement.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight;
  
  // Only apply orientation lock on mobile/tablet devices (width <= 1024px)
  const isMobileOrTablet = width <= 1024;
  if (!isMobileOrTablet) {
    return false; // Never show orientation message on desktop
  }
  
  // Check if it's landscape based on dimensions
  const isLandscapeByDimensions = width > height;
  
  // Check using screen orientation API if available
  let isLandscapeByOrientation = false;
  if (window.screen && (window.screen as any).orientation) {
    const orientation = (window.screen as any).orientation.type;
    isLandscapeByOrientation = orientation.includes('landscape');
  }
  
  // Check using window.orientation (legacy Android support)
  let isLandscapeByWindowOrientation = false;
  if (typeof window.orientation !== 'undefined') {
    isLandscapeByWindowOrientation = Math.abs(window.orientation) === 90;
  }
  
  // Return true if any method detects landscape (on mobile/tablet only)
  return isLandscapeByDimensions || isLandscapeByOrientation || isLandscapeByWindowOrientation;
};

function App() {
  const [album, setAlbum] = useState<Album | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  // Enhanced orientation detection for mobile compatibility
  useEffect(() => {
    const checkOrientation = () => {
      const isLandscape = isMobileLandscape();
      setIsLandscape(isLandscape);
    };

    // Check initial orientation with multiple attempts for Android
    const initialCheck = () => {
      checkOrientation();
      // Multiple checks with different delays for Android devices
      setTimeout(checkOrientation, 50);
      setTimeout(checkOrientation, 150);
      setTimeout(checkOrientation, 300);
    };
    
    initialCheck();

    // Listen for orientation changes with multiple event types for Android
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    // Android-specific orientation events
    if (window.screen && (window.screen as any).orientation) {
      (window.screen as any).orientation.addEventListener('change', checkOrientation);
    }
    
    // Additional Android fallbacks with different timing
    window.addEventListener('orientationchange', () => {
      setTimeout(checkOrientation, 100);
      setTimeout(checkOrientation, 300);
    });

    // Legacy Android support
    if (typeof window.orientation !== 'undefined') {
      window.addEventListener('orientationchange', () => {
        setTimeout(checkOrientation, 200);
      });
    }

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
      if (window.screen && (window.screen as any).orientation) {
        (window.screen as any).orientation.removeEventListener('change', checkOrientation);
      }
    };
  }, []);

  useEffect(() => {
    const loadEventData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get event code from URL
        const eventCode = getEventCodeFromUrl();
        
        if (!eventCode) {
          throw new Error('No event code found in URL. Please provide a valid event code.');
        }

        // Fetch event data from API
        const apiData = await ApiService.getEventDetails(eventCode);
        console.log('API Response received in App.tsx:', apiData);
        
        // Transform API data to our Album format
        const albumData = ApiService.transformApiDataToAlbum(apiData);
        console.log('Transformed Album Data:', albumData);
        
        setAlbum(albumData);
      } catch (err) {
        console.error('Error loading event data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load event data');
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, []);

  // Update meta tags when album data loads
  useEffect(() => {
    if (album) {
      const metaData = generateEventMetaData(album);
      updateMetaTags(metaData);
    } else {
      // Set default meta tags
      setDefaultMetaTags();
    }
  }, [album]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading event...</p>
      </div>
    );
  }

  // Handle error cases with modal
  if (error && !album) {
    return (
      <div className="App">
        <ErrorModal
          isOpen={showErrorModal}
          error={error}
          onClose={() => setShowErrorModal(false)}
          onGoToApp={() => window.location.href = 'https://app.fiestalbum.com'}
        />
      </div>
    );
  }

  if (!album) {
    return (
      <div className="App">
        <ErrorModal
          isOpen={showErrorModal}
          error="No event data available"
          onClose={() => setShowErrorModal(false)}
          onGoToApp={() => window.location.href = 'https://app.fiestalbum.com'}
        />
      </div>
    );
  }

  // Show orientation message if in landscape mode
  if (isLandscape) {
    return (
      <div className="orientation-message">
        <div className="phone-icon">📱</div>
        <h2>Please Rotate Your Device</h2>
        <p>FiestAlbum Live View is optimized for portrait mode. Please rotate your device to portrait orientation for the best experience.</p>
      </div>
    );
  }

  return (
    <div className="App">
      <AlbumView album={album} />
    </div>
  );
}

export default App;
